import React, { useState } from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import DashboardParityDetailsView
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__parity/page/view/DashboardParityDailyView';
import DashboardParityReportView
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__parity/page/view/DashboardParityReportView';
import MarketStateParityForPeriodProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/MarketStateParityForPeriodProvider';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import RatesType from '@app/model/market_state/RatesType';
import Region from '@app/model/market_state/Region';
import ParityFiltersHeaderFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__parity/fragment/ParityFiltersHeaderFragment';
import CalendarRange from '@app/state/calendar/CalendarRange';
import ParityFilterInput from '@app/model/parity/input/ParityFilterInput';
import OtaPricesForPeriodProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/OtaPricesForPeriodProvider';
import MarketStateSnapshotUpdateDate
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateSnapshotUpdateDate';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import {
	RefreshMarketStateButton,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/RefreshMarketStateButton';
import Refunding from '@app/model/market_state/Refunding';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketStateToolsProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/MarketStateToolsProvider';
import ParityExportToExcelSelectorFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__parity/fragment/ParityExportToExcelSelectorFragment';
import SiteNotificationFragment from '@app/site/secured/_common/layout/fragment/SiteNotificationFragment';


const ParityPage = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();

	if ((companyHotel.settings?.defaultParsing?.nights?.length ?? 0) > 0) {
		return <MarketStateToolsProvider companyHotel={companyHotel}>
			<RenderDashboardParityPage companyHotel={companyHotel} />
		</MarketStateToolsProvider>;
	}
	return <></>;
});

const RenderDashboardParityPage: React.FunctionComponent<{
	companyHotel: CompanyHotel
}> = ({ companyHotel }) => {

	const [tab, setTab] = useState<'DAILY' | 'REPORT'>('DAILY');

	const activeClassNames = 'whitespace-nowrap border-b-2 border-blue-500 px-3 py-1.5 text-sm font-medium text-blue-600 cursor-pointer';
	const inActiveClassNames = 'whitespace-nowrap border-b-2 border-transparent px-3 py-1.5 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 cursor-pointer';
	const [range, setRange] = useState<CalendarRange>();
	const [filter, setFilter] = useState<ParityFilterInput>({
		roomTypes: [],
		meal: undefined,
		guests: 2,
		nights: companyHotel.settings.defaultParsing.nights[0],
		refunding: Refunding.NONE,
		ratesType: RatesType.PUBLIC,
		region: Region.EUROPE,
		competitorTagIds: [],
		airportCode: undefined,
		compareAirportCodes: []
	});

	const tools = useMarketStateTools();


	return <>
		<ParityFiltersHeaderFragment
			companyHotel={companyHotel}
			setRange={setRange}
			filter={filter}
			setFilter={setFilter}
		/>
		<main>
			<div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8 items-center">
				<SiteNotificationFragment />
				<div className="lg:flex lg:h-full lg:flex-col">
					<div className="flex items-center justify-between pb-4 lg:flex-none">
						<div className="flex items-center flex-wrap gap-x-2 gap-y-2"><span
							className="text-sm font-semibold leading-6 text-gray-900">Filters Applied</span>
							<div className="h-4 w-px bg-gray-300 mx-1 hidden sm:block"></div>
							<span className="inline-flex items-center rounded-lg bg-slate-50 px-4 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-inset ring-slate-700/20">Lowest</span>
							<span className="inline-flex items-center rounded-lg bg-slate-50 px-4 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-inset ring-slate-700/20">Any Room</span>
							<span className="inline-flex items-center rounded-lg bg-slate-50 px-4 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-inset ring-slate-700/20">Any Meal</span>
							<span className="inline-flex items-center rounded-lg bg-slate-50 px-4 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-inset ring-slate-700/20">{filter.guests} Guests</span>
						</div>
						<div className="mb-6 flex items-center flex-wrap gap-y-2 gap-x-2">
							<MarketStateSnapshotUpdateDate calendarMonth={tools.calendarMonth} />
							<ParityExportToExcelSelectorFragment
								companyHotel={companyHotel}
								calendarMonth={tools.calendarMonth}
								filter={filter}
							/>
							<RefreshMarketStateButton
								companyHotel={companyHotel}
								calendarMonth={tools.calendarMonth}
								filter={filter}
								withOta
							/>
						</div>
					</div>
				</div>
				<div className="mb-6">
					<div className="grid grid-cols-1 sm:hidden">
						<select
							aria-label="Select a tab"
							value={tab}
							className="col-start-1 row-start-1 w-full input"
							onChange={e => setTab(e.target.value as 'DAILY' | 'REPORT')}
						>
							<option value="DAILY">Daily Review</option>
							<option value="REPORT">Reports</option>
						</select>
					</div>
					<div className="hidden sm:block">
						<div className="border-b border-gray-200">
							<nav className="-mb-px flex" aria-label="Tabs">
								<a onClick={() => setTab('DAILY')} className={tab === 'DAILY' ? activeClassNames : inActiveClassNames}>
									Daily Review
								</a>
								<a onClick={() => setTab('REPORT')} className={tab === 'REPORT' ? activeClassNames : inActiveClassNames}>
									Reports
								</a>
							</nav>
						</div>
					</div>
				</div>
				{range !== undefined && <MarketStateParityForPeriodProvider companyHotel={companyHotel}
																												parityFilter={filter}
																												loadingPlaceholder={<TablePlaceholder />}
																												range={range}>
					<OtaPricesForPeriodProvider companyHotel={companyHotel}
																			parityFilter={filter}
																			range={range}>
						{tab === 'DAILY' && <DashboardParityDetailsView filter={filter}/>}
						{tab === 'REPORT' && <DashboardParityReportView companyHotel={companyHotel} />}
					</OtaPricesForPeriodProvider>
				</MarketStateParityForPeriodProvider>}
			</div>
		</main>
	</>;
};

export default ParityPage;
